import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add, CurrencyRupee, Edit } from '@mui/icons-material';
import { classFeesAPI } from '../../services/api';

const classPresets = [
  { standard: 'Nursery', display_name: 'Nursery', sort_order: 1 },
  { standard: 'LKG', display_name: 'LKG', sort_order: 2 },
  { standard: 'UKG', display_name: 'UKG', sort_order: 3 },
  { standard: '1', display_name: 'Class 1', sort_order: 4 },
  { standard: '2', display_name: 'Class 2', sort_order: 5 },
  { standard: '3', display_name: 'Class 3', sort_order: 6 },
  { standard: '4', display_name: 'Class 4', sort_order: 7 },
  { standard: '5', display_name: 'Class 5', sort_order: 8 },
  { standard: '6', display_name: 'Class 6', sort_order: 9 },
  { standard: '7', display_name: 'Class 7', sort_order: 10 },
  { standard: '8', display_name: 'Class 8', sort_order: 11 },
  { standard: '9', display_name: 'Class 9', sort_order: 12 },
  { standard: '10', display_name: 'Class 10', sort_order: 13 },
];

const initialClassForm = {
  standard: '',
  display_name: '',
  sort_order: '',
  is_active: true,
};

const initialFeeForm = {
  standard: '',
  fee_type: 'Tuition',
  amount: '',
  is_mandatory: true,
};

const initialVehicleFeeForm = {
  vehicle_name: '',
  route_name: '',
  amount: '',
  is_active: true,
};

const ClassFees = () => {
  const [classes, setClasses] = useState([]);
  const [fees, setFees] = useState([]);
  const [vehicleFees, setVehicleFees] = useState([]);
  const [classForm, setClassForm] = useState(initialClassForm);
  const [feeForm, setFeeForm] = useState(initialFeeForm);
  const [vehicleFeeForm, setVehicleFeeForm] = useState(initialVehicleFeeForm);
  const [editingVehicleFeeId, setEditingVehicleFeeId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingClass, setSavingClass] = useState(false);
  const [savingFee, setSavingFee] = useState(false);
  const [savingVehicleFee, setSavingVehicleFee] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const activeClasses = useMemo(
    () => classes.filter((cls) => Number(cls.is_active) === 1),
    [classes]
  );

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await classFeesAPI.getSettings();
      setClasses(Array.isArray(response.data.classes) ? response.data.classes : []);
      setFees(Array.isArray(response.data.fees) ? response.data.fees : []);
      setVehicleFees(Array.isArray(response.data.vehicleFees) ? response.data.vehicleFees : []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load class and fee settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const handleClassPreset = (event) => {
    const selected = classPresets.find((preset) => preset.standard === event.target.value);
    if (!selected) return;

    setClassForm({
      standard: selected.standard,
      display_name: selected.display_name,
      sort_order: selected.sort_order,
      is_active: true,
    });
  };

  const handleClassChange = (event) => {
    const { name, value, checked, type } = event.target;
    setClassForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleFeeChange = (event) => {
    const { name, value, checked, type } = event.target;
    setFeeForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleVehicleFeeChange = (event) => {
    const { name, value, checked, type } = event.target;
    setVehicleFeeForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleAddClass = async (event) => {
    event.preventDefault();

    if (!classForm.standard || !classForm.display_name) {
      setError('Class code and display name are required.');
      return;
    }

    try {
      setSavingClass(true);
      setError('');
      setSuccess('');
      await classFeesAPI.createClass({
        ...classForm,
        sort_order: classForm.sort_order || 0,
        is_active: classForm.is_active ? 1 : 0,
      });
      setSuccess('Class added successfully.');
      setClassForm(initialClassForm);
      await loadSettings();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add class');
    } finally {
      setSavingClass(false);
    }
  };

  const handleAddFee = async (event) => {
    event.preventDefault();

    if (!feeForm.standard || !feeForm.fee_type || feeForm.amount === '') {
      setError('Class, fee type and amount are required.');
      return;
    }

    try {
      setSavingFee(true);
      setError('');
      setSuccess('');
      await classFeesAPI.createFee({
        ...feeForm,
        is_mandatory: feeForm.is_mandatory ? 1 : 0,
      });
      setSuccess('Fee added successfully.');
      setFeeForm(initialFeeForm);
      await loadSettings();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add fee');
    } finally {
      setSavingFee(false);
    }
  };

  const handleSaveVehicleFee = async (event) => {
    event.preventDefault();

    if (!vehicleFeeForm.vehicle_name || vehicleFeeForm.amount === '') {
      setError('Vehicle name and amount are required.');
      return;
    }

    try {
      setSavingVehicleFee(true);
      setError('');
      setSuccess('');
      const payload = {
        ...vehicleFeeForm,
        is_active: vehicleFeeForm.is_active ? 1 : 0,
      };

      if (editingVehicleFeeId) {
        await classFeesAPI.updateVehicleFee(editingVehicleFeeId, payload);
        setSuccess('Vehicle fee updated successfully.');
      } else {
        await classFeesAPI.createVehicleFee(payload);
        setSuccess('Vehicle fee added successfully.');
      }

      setVehicleFeeForm(initialVehicleFeeForm);
      setEditingVehicleFeeId(null);
      await loadSettings();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save vehicle fee');
    } finally {
      setSavingVehicleFee(false);
    }
  };

  const handleEditVehicleFee = (vehicleFee) => {
    setEditingVehicleFeeId(vehicleFee.id);
    setVehicleFeeForm({
      vehicle_name: vehicleFee.vehicle_name || '',
      route_name: vehicleFee.route_name || '',
      amount: vehicleFee.amount || '',
      is_active: Number(vehicleFee.is_active) === 1,
    });
  };

  const handleCancelVehicleEdit = () => {
    setEditingVehicleFeeId(null);
    setVehicleFeeForm(initialVehicleFeeForm);
  };

  const classNameFor = (standard) => {
    const match = classes.find((cls) => cls.standard === standard);
    return match ? match.display_name : standard;
  };

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Typography variant="h5" fontWeight="bold">
          Class & Fees
        </Typography>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} lg={5}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Available Classes
              </Typography>

              <Box component="form" onSubmit={handleAddClass} sx={{ mb: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      select
                      label="Quick Select"
                      value={classForm.standard}
                      onChange={handleClassPreset}
                      fullWidth
                    >
                      {classPresets.map((preset) => (
                        <MenuItem key={preset.standard} value={preset.standard}>
                          {preset.display_name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="standard"
                      label="Class Code"
                      value={classForm.standard}
                      onChange={handleClassChange}
                      required
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="display_name"
                      label="Display Name"
                      value={classForm.display_name}
                      onChange={handleClassChange}
                      required
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="sort_order"
                      label="Order"
                      type="number"
                      value={classForm.sort_order}
                      onChange={handleClassChange}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="is_active"
                          checked={classForm.is_active}
                          onChange={handleClassChange}
                        />
                      }
                      label="Active"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={<Add />}
                      disabled={savingClass}
                      fullWidth
                    >
                      {savingClass ? 'Saving...' : 'Add Class'}
                    </Button>
                  </Grid>
                </Grid>
              </Box>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Class</TableCell>
                      <TableCell>Code</TableCell>
                      <TableCell>Order</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {classes.length > 0 ? (
                      classes.map((cls) => (
                        <TableRow key={cls.id} hover>
                          <TableCell>{cls.display_name}</TableCell>
                          <TableCell>{cls.standard}</TableCell>
                          <TableCell>{cls.sort_order}</TableCell>
                          <TableCell>{Number(cls.is_active) === 1 ? 'Active' : 'Inactive'}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4}>No classes added yet.</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          <Grid item xs={12} lg={7}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Class-wise Fees
              </Typography>

              <Box component="form" onSubmit={handleAddFee} sx={{ mb: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      name="standard"
                      label="Class"
                      value={feeForm.standard}
                      onChange={handleFeeChange}
                      required
                      fullWidth
                    >
                      {activeClasses.map((cls) => (
                        <MenuItem key={cls.id} value={cls.standard}>
                          {cls.display_name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      name="fee_type"
                      label="Fee Type"
                      value={feeForm.fee_type}
                      onChange={handleFeeChange}
                      required
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      name="amount"
                      label="Amount"
                      type="number"
                      value={feeForm.amount}
                      onChange={handleFeeChange}
                      required
                      fullWidth
                      inputProps={{ min: 0, step: '0.01' }}
                      InputProps={{ startAdornment: <CurrencyRupee fontSize="small" /> }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="is_mandatory"
                          checked={feeForm.is_mandatory}
                          onChange={handleFeeChange}
                        />
                      }
                      label="Mandatory"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={<Add />}
                      disabled={savingFee || activeClasses.length === 0}
                      fullWidth
                    >
                      {savingFee ? 'Saving...' : 'Add Fee'}
                    </Button>
                  </Grid>
                </Grid>
              </Box>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Class</TableCell>
                      <TableCell>Fee Type</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Academic Year</TableCell>
                      <TableCell>Mandatory</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fees.length > 0 ? (
                      fees.map((fee) => (
                        <TableRow key={fee.id} hover>
                          <TableCell>{classNameFor(fee.standard)}</TableCell>
                          <TableCell>{fee.fee_type}</TableCell>
                          <TableCell>{Number(fee.amount).toFixed(2)}</TableCell>
                          <TableCell>{fee.year_name || '-'}</TableCell>
                          <TableCell>{Number(fee.is_mandatory) === 1 ? 'Yes' : 'No'}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5}>No fees added yet.</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Vehicle Fees
              </Typography>

              <Box component="form" onSubmit={handleSaveVehicleFee} sx={{ mb: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      name="vehicle_name"
                      label="Vehicle Name"
                      value={vehicleFeeForm.vehicle_name}
                      onChange={handleVehicleFeeChange}
                      required
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      name="route_name"
                      label="Route / Area"
                      value={vehicleFeeForm.route_name}
                      onChange={handleVehicleFeeChange}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      name="amount"
                      label="Amount"
                      type="number"
                      value={vehicleFeeForm.amount}
                      onChange={handleVehicleFeeChange}
                      required
                      fullWidth
                      inputProps={{ min: 0, step: '0.01' }}
                      InputProps={{ startAdornment: <CurrencyRupee fontSize="small" /> }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="is_active"
                          checked={vehicleFeeForm.is_active}
                          onChange={handleVehicleFeeChange}
                        />
                      }
                      label="Active"
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Stack direction="row" spacing={1}>
                      <Button
                        type="submit"
                        variant="contained"
                        startIcon={editingVehicleFeeId ? <Edit /> : <Add />}
                        disabled={savingVehicleFee}
                        fullWidth
                      >
                        {savingVehicleFee ? 'Saving...' : editingVehicleFeeId ? 'Update' : 'Add'}
                      </Button>
                      {editingVehicleFeeId && (
                        <Button onClick={handleCancelVehicleEdit} disabled={savingVehicleFee}>
                          Cancel
                        </Button>
                      )}
                    </Stack>
                  </Grid>
                </Grid>
              </Box>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Vehicle</TableCell>
                      <TableCell>Route / Area</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vehicleFees.length > 0 ? (
                      vehicleFees.map((vehicleFee) => (
                        <TableRow key={vehicleFee.id} hover>
                          <TableCell>{vehicleFee.vehicle_name}</TableCell>
                          <TableCell>{vehicleFee.route_name || '-'}</TableCell>
                          <TableCell>{Number(vehicleFee.amount).toFixed(2)}</TableCell>
                          <TableCell>{Number(vehicleFee.is_active) === 1 ? 'Active' : 'Inactive'}</TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              startIcon={<Edit />}
                              onClick={() => handleEditVehicleFee(vehicleFee)}
                            >
                              Edit
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5}>No vehicle fees added yet.</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default ClassFees;
